import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  type Billing,
  billingOf,
  formatDateTimeIST,
  formatRupees,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  PAYMENT_STATE_LABELS,
  type PaymentMethod,
  type PaymentState,
  type PriceItem,
  toPaise,
} from '@quickreport/domain';
import { Badge, Button, Field, Input, Select } from '@quickreport/ui';
import { addPayment, removePayment, setReportPricing } from '@quickreport/workspace';
import { Lock, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useWorkspace } from '../../lib/workspace';
import { useCtx } from './context';

export const PAY_TONE: Record<PaymentState, 'neutral' | 'brand' | 'flag' | 'ok'> = {
  free: 'neutral',
  unpaid: 'flag',
  part: 'brand',
  paid: 'ok',
  over: 'ok',
};

interface PayRow {
  id: string;
  amount_paise: number;
  method: PaymentMethod;
  paid_at: string;
  note: string | null;
}

/** Live price, payments and what is still to pay for one report. */
export function useBilling(reportId: string): {
  billing: Billing;
  items: PriceItem[];
  payments: PayRow[];
} | null {
  const rep = useLiveQuery<{
    price_paise: number;
    discount_paise: number;
    price_items: PriceItem[];
  }>('select price_paise, discount_paise, price_items from reports where id = $1', [reportId]);
  const pays = useLiveQuery<PayRow>(
    `select id, amount_paise, method, paid_at, note from payments
      where report_id = $1 and deleted_at is null order by paid_at, created_at`,
    [reportId],
  );
  const r = rep?.rows[0];
  if (!r || !pays) return null;
  const paid = pays.rows.reduce((n, p) => n + Number(p.amount_paise), 0);
  return {
    billing: billingOf(Number(r.price_paise), Number(r.discount_paise), paid),
    items: r.price_items ?? [],
    payments: pays.rows,
  };
}

const local = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Amount received for a report, compared with the price kept when its tests were chosen. */
export function PaymentPanel({ reportId }: { reportId: string }) {
  const ws = useWorkspace();
  const ctx = useCtx();
  const data = useBilling(reportId);
  const canManage = ws.can('manage_billing');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const [discount, setDiscount] = useState<string | null>(null);
  const [price, setPrice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!data) return <p className="p-4 text-slate">Loading…</p>;
  const { billing: b, items, payments } = data;

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      void ws.engine.syncNow();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    }
  }

  const add = (paise: number) =>
    run(async () => {
      await addPayment(ws.db, ctx, reportId, {
        amountPaise: paise,
        method,
        ...(at ? { paidAt: new Date(at).toISOString() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setAmount('');
      setAt('');
      setNote('');
    });

  return (
    <div className="grid gap-5 p-4 lg:grid-cols-[1fr_1fr]">
      <p className="flex items-center gap-2 rounded-md bg-canvas px-3 py-2 text-[13px] text-slate lg:col-span-2">
        <Lock size={14} /> Prices and payments are private to the lab. They are never printed on the
        report or shown to the patient.
      </p>

      <section className="grid content-start gap-3" aria-label="Price">
        <div className="flex items-center justify-between">
          <h3 className="text-[13px] font-semibold">Price</h3>
          <Badge tone={PAY_TONE[b.state]}>{PAYMENT_STATE_LABELS[b.state]}</Badge>
        </div>
        <p className="text-[12px] text-slate">
          Kept from when the tests were chosen. Changing a template later does not change it.
        </p>
        <table className="w-full text-[13px]">
          <tbody>
            {items.map((i) => (
              <tr
                key={`${i.templateId ?? i.name}-${i.pricePaise}`}
                className="border-b border-line"
              >
                <td className="py-1.5">{i.name}</td>
                <td className="tabular py-1.5 text-right">{formatRupees(i.pricePaise)}</td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td className="py-1.5 text-slate">No priced tests</td>
                <td />
              </tr>
            ) : null}
            <tr>
              <td className="pt-2 text-slate">Total price</td>
              <td className="tabular pt-2 text-right">{formatRupees(b.pricePaise)}</td>
            </tr>
            <tr>
              <td className="text-slate">Discount</td>
              <td className="tabular text-right">
                {b.discountPaise ? `− ${formatRupees(b.discountPaise)}` : '—'}
              </td>
            </tr>
            <tr className="font-semibold">
              <td className="pt-1">To pay</td>
              <td className="tabular pt-1 text-right">{formatRupees(b.duePaise)}</td>
            </tr>
            <tr>
              <td className="text-slate">Received</td>
              <td className="tabular text-right">{formatRupees(b.paidPaise)}</td>
            </tr>
            <tr className="font-semibold">
              <td>{b.excessPaise > 0 ? 'Paid extra' : 'Balance'}</td>
              <td className={`tabular text-right ${b.balancePaise > 0 ? 'text-flag' : 'text-ok'}`}>
                {formatRupees(b.excessPaise > 0 ? b.excessPaise : b.balancePaise)}
              </td>
            </tr>
          </tbody>
        </table>
        {canManage ? (
          <div className="grid grid-cols-2 items-end gap-2">
            <Field label="Change total (₹)">
              <Input
                inputMode="decimal"
                value={price ?? String(b.pricePaise / 100)}
                onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))}
              />
            </Field>
            <Field label="Discount (₹)">
              <Input
                inputMode="decimal"
                value={discount ?? String(b.discountPaise / 100)}
                onChange={(e) => setDiscount(e.target.value.replace(/[^\d.]/g, ''))}
              />
            </Field>
            <Button
              className="col-span-2 justify-self-start"
              disabled={price === null && discount === null}
              onClick={() =>
                void run(async () => {
                  await setReportPricing(ws.db, ctx, reportId, {
                    ...(price !== null ? { pricePaise: toPaise(Number(price) || 0) } : {}),
                    ...(discount !== null ? { discountPaise: toPaise(Number(discount) || 0) } : {}),
                  });
                  setPrice(null);
                  setDiscount(null);
                })
              }
            >
              Save price
            </Button>
          </div>
        ) : null}
      </section>

      <section className="grid content-start gap-3" aria-label="Payments">
        <h3 className="text-[13px] font-semibold">Amount received</h3>
        {payments.length === 0 ? (
          <p className="text-[13px] text-slate">Nothing received yet.</p>
        ) : (
          <ul className="grid gap-1.5">
            {payments.map((p) => (
              <li
                key={p.id}
                className="flex items-center gap-3 rounded-md bg-canvas px-3 py-2 text-[13px]"
              >
                <span className="tabular w-24 font-medium">{formatRupees(p.amount_paise)}</span>
                <span className="min-w-0 flex-1 text-slate">
                  {PAYMENT_METHOD_LABELS[p.method] ?? p.method} · {formatDateTimeIST(p.paid_at)}
                  {p.note ? ` · ${p.note}` : ''}
                </span>
                {canManage ? (
                  <button
                    type="button"
                    aria-label="Remove this payment"
                    title="Remove this payment"
                    className="rounded p-1 text-slate hover:text-flag"
                    onClick={() => {
                      if (
                        window.confirm('Remove this payment? Enter it again if it was a mistake.')
                      )
                        void run(() => removePayment(ws.db, ctx, p.id));
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canManage ? (
          <div className="grid gap-2 rounded-lg border border-line p-3">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Amount (₹)">
                <Input
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                />
              </Field>
              <Field label="Paid by">
                <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="When" hint="Empty = now">
                <Input
                  type="datetime-local"
                  value={at}
                  max={local(new Date(Date.now() + 86_400_000))}
                  onChange={(e) => setAt(e.target.value)}
                />
              </Field>
              <Field label="Note">
                <Input value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                tone="primary"
                disabled={!(Number(amount) > 0)}
                onClick={() => void add(toPaise(Number(amount)))}
              >
                Add payment
              </Button>
              {b.balancePaise > 0 ? (
                <Button onClick={() => void add(b.balancePaise)}>
                  Received full balance ({formatRupees(b.balancePaise)})
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
            {error}
          </p>
        ) : null}
      </section>
    </div>
  );
}
